<script setup>
  import { computed } from 'vue';

  import { useUserStore } from '@/stores/UserStore';
  import { useSpotStore } from '@/stores/SpotStore';
  import { useGoogleAnalyticsStore } from '@/stores/GoogleAnalyticsStore';

  import Icon from "@/components/icons/Icon.vue";
  import { findMatchingProfileContainers } from '@/helpers/findMatchingProfileContainer';

  const emit = defineEmits(['back']);

  const savedContainerName = defineModel('savedContainerName');

  const props = defineProps({
    containerTypeId: {
      type: String,
      required: true
    },  
    containerTypeName: {
      type: String,
      required: true
    },
    contextName: {
      type: String,
      required: false
    },
    settings: {
      type: Object,
      required: true
    },
    closeModal: {
      type: Function,
      required: true
    }
  });

  const spotStore = useSpotStore();
  const userStore = useUserStore();
  const googleAnalyticsStore = useGoogleAnalyticsStore();

  const saveToProfileHeader = computed(() => `Save ${props.contextName || spotStore.spotName} ${props.containerTypeName} to Profile.`);
  const saveToProfileInstructions = computed(() => `Choose the profile${userStore.userProfiles.profiles.length > 1 ? 's' : ''} you would like to save <strong>${savedContainerName.value}</strong> to.`);
  const profileSelections = computed(() => userStore.userProfiles.profiles.map(profile => ({
    profile,
    savedContainers: findMatchingProfileContainers(profile, props.containerTypeId, props.settings)
  })));

  function handleBack() {
    emit('back');
  }

  async function handleContainerProfileSelection(event) {
    if (event.target.checked) {
      const data = await userStore.addContainerToProfile(event.target.value, props.containerTypeId, savedContainerName.value, props.settings);

      if (props.containerTypeId === 'Map') {
        googleAnalyticsStore.sendEvent('Favorite_Map_Add');
      } else {
        googleAnalyticsStore.sendEvent('Favorite_Container_Add', {
          Type: props.containerTypeId,
        });
      }

      if (data?.status_code === 0) {
        event.target.setAttribute('data-container-id', data.status_message);
      }
    } else {
      const profileSelection = profileSelections.value.find(({ profile }) => (
        String(profile.profile_id) === String(event.target.value)
      ));

      await Promise.all(profileSelection.savedContainers.map(container => (
        userStore.removeContainerFromProfile(container.container_id)
      )));

      event.target.removeAttribute('data-container-id');
    }

    await userStore.getProfiles(true);
    userStore.refreshCurrentProfile();
  }  
</script>

<template>
  <header class="jwx-modal__header">
    <h2 class="jwx-modal__title">{{ saveToProfileHeader }}</h2>
  </header>          
  <div class="jwx-modal__body">
    <div class="jwx-modal__section">
      <p v-html="saveToProfileInstructions" class="jwx-modal__text"></p>
    </div>
    <div class="jwx-modal__section">
      <ul class="jwx-save-to-selector">
        <li v-for="{ profile, savedContainers } in profileSelections" class="jwx-save-to-selector__item" :key="profile.profile_id">
          <input
            @change="handleContainerProfileSelection"
            type="checkbox"
            :name="`jwx-${profile.profile_id}`"
            :id="`jwx-${profile.profile_id}`"
            :value="profile.profile_id"
            :checked="savedContainers.length > 0"
            :data-container-id="savedContainers[0]?.container_id"
            class="jwx-save-to-selector__input jwx-hidden-control"
          />
          <label :for="`jwx-${profile.profile_id}`" class="jwx-save-to-selector__label">
            <h4 class="jwx-save-to-selector__label-title">
              {{ profile.name }}
            </h4>
            <div class="jwx-save-to-selector__selection-indicator">
              <Icon name="checkmark" class="jwx-save-to-selector__indicator-icon" />
            </div>
          </label>
        </li>
      </ul>
    </div>
  </div>
  <div class="jwx-modal__footer">
    <div class="jwx-modal__actions">
      <button
        type="button"
        @click.prevent="handleBack"
        class="jwx-button"
      >
        Back
      </button>
      <button 
        type="button" 
        @click.prevent="closeModal"
        class="jwx-button jwx-button--success"
      >
        Done
      </button>
    </div>
  </div>
</template>
